import { FieldValues, Resolver } from 'react-hook-form';

import { useEventHandler } from '../useEventHandler';
import { useRootDocument } from '../useRootDocument';
import { handleResolverFromConfig } from './logic/handleResolverFromConfig';
import { FormResolverConfig } from './types/FormResolverConfig';

/**
 * Returns a React Hook Form resolver wired to Yup validation.
 *
 * Two calling conventions:
 *
 * 1. Static schema — pass any `AnyObjectSchema` directly.
 *    Use this when your validation rules are fixed at render time (middle-office
 *    modals, broker-portal forms, anything outside a workflow step). The schema
 *    is evaluated exactly as if you'd called `yupResolver(schema)` yourself.
 *
 *      const resolver = useFormResolver(yup.object({ field: yup.string().required() }));
 *
 * 2. Dynamic blocks — pass an array of `FormBlockConfig`.
 *    Use this inside workflow steps where field visibility/requirement depends on
 *    other field values. The schema is rebuilt on every validation call so
 *    `displayWhen` conditions always see the current values.
 *
 *    Flat and namespaced configs can be freely mixed in the same array. Flat fields
 *    are merged into the top-level schema alongside any namespaced fields — they are
 *    validated against `values[fieldName]` directly, not nested under a key.
 *
 *    - Flat (no namespace): no `namespace` property. Fields are validated at the
 *      top level of the form values — getter is `get(values, key)`.
 *
 *        const resolver = useFormResolver([{ formFieldBlocks }]);
 *
 *        // mix with namespaced configs — flat fields sit alongside namespaced keys:
 *        const resolver = useFormResolver([
 *          { formFieldBlocks: assetBlocks, namespace: 'asset' },
 *          { formFieldBlocks: declarationBlocks },  // merged into top-level shape
 *        ]);
 *
 *    - Namespaced: has a `namespace` property. Form values must be structured as
 *      `{ [namespace]: fieldValues }` — used when a single form covers multiple
 *      independently-validated block groups (e.g. asset + quote). The getter for
 *      each namespace is scoped to `values[namespace]`.
 *
 *        const resolver = useFormResolver([
 *          { formFieldBlocks: assetBlocks, namespace: 'asset' },
 *          { formFieldBlocks: quoteBlocks, namespace: 'quote' },
 *        ]);
 *
 *    - `repeat: true` wraps the namespace schema in `yup.array().of(...).required()`.
 *      The schema for each item is built lazily using `yup.lazy()`, so `displayWhen`
 *      conditions are evaluated with values scoped to that specific array item —
 *      each element is validated independently.
 *
 *        const resolver = useFormResolver([
 *          { formFieldBlocks, namespace: 'financialSources', repeat: true },
 *        ]);
 *
 *    - `repeat: string[]` produces a keyed object where each key maps to a lazily-
 *      validated array. Use this when the namespace holds an object of typed arrays
 *      rather than a flat array (e.g. assets grouped by type).
 *
 *        const resolver = useFormResolver([{
 *          formFieldBlocks: assetBlocks,
 *          namespace: 'assets',
 *          repeat: ['realEstate', 'vehicle', 'savings'],
 *          // produces: { realEstate: item[], vehicle: item[], savings: item[] }
 *        }]);
 *
 *    - `getFormValue`: an optional override for the value getter used when evaluating
 *      `displayWhen` conditions during schema building. Receives the full form values
 *      so you can cross-reference other namespaces. Use this when the `displayWhen`
 *      logic for one namespace depends on a value stored in a different namespace.
 *
 *        const resolver = useFormResolver([
 *          { formFieldBlocks: primaryBlocks, namespace: 'primary', repeat: true },
 *          {
 *            formFieldBlocks: partnerBlocks,
 *            namespace: 'partner',
 *            // partner field visibility depends on primary[0].isUsingPartnerOffset
 *            getFormValue: (values, key) => get(values, ['primary', 0, key]),
 *          },
 *        ]);
 *
 *    - Children: use `children` instead of `formFieldBlocks` when each item in the
 *      namespace array is itself an object containing multiple named array fields,
 *      each validated by their own block schema. `repeat: true` is required (and
 *      enforced by the type) when using `children`. Each child's array items are
 *      also lazy-evaluated, so `displayWhen` conditions see the child item's own
 *      values.
 *
 *        const resolver = useFormResolver([{
 *          namespace: 'employmentApplicants',
 *          repeat: true,
 *          children: [
 *            { namespace: 'employment', formFieldBlocks: employmentBlocks,      repeat: true },
 *            { namespace: 'income',     formFieldBlocks: additionalIncomeBlocks, repeat: true },
 *            { namespace: 'benefits',   formFieldBlocks: benefitsBlocks,         repeat: true },
 *          ],
 *        }]);
 *
 * @See We should be looking to move consistently towards the dynamic blocks convention across our codebase
 */
export const useFormResolver = <TFieldValues extends FieldValues>(
  formResolverConfig: FormResolverConfig,
): Resolver<TFieldValues> => {
  const rootDocument = useRootDocument();

  const resolver = useEventHandler(
    handleResolverFromConfig<TFieldValues>(formResolverConfig, rootDocument),
  );

  return resolver;
};
